import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { CheckCircle2, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { resetPassword } from '../api/auth-api'

const forgotPasswordSchema = z.object({
  email: z.string().email('ایمیل نامعتبر است'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [sent, setSent] = useState(false)

  const mutation = useMutation({
    mutationFn: (email: string) => resetPassword(email),
    onSuccess: () => setSent(true),
  })

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = (data: ForgotPasswordFormData) => {
    mutation.mutate(data.email)
  }

  return (
    <div className="relative min-h-screen bg-background flex items-center justify-center overflow-hidden">
      {/* Background blobs */}
      <div
        className="absolute top-0 start-0 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'rgba(39,70,144,0.20)', transform: 'translate(-30%, -30%)' }}
      />
      <div
        className="absolute bottom-0 end-0 w-80 h-80 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'rgba(40,111,108,0.20)', transform: 'translate(30%, 30%)' }}
      />

      <div className="relative z-10 w-full max-w-[420px] mx-4">
        <div className="bg-card border border-border rounded-xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mb-4">
              <span className="text-primary-foreground font-bold text-xl font-mono">M</span>
            </div>
            <h1 className="text-xl font-bold text-foreground">
              {t('appTitle')}
            </h1>
          </div>

          {sent ? (
            /* Success state */
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-secondary" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-base font-semibold text-foreground">
                  {t('auth.forgotPassword.sentTitle', 'ایمیل ارسال شد')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'auth.forgotPassword.sentBody',
                    'لینک بازیابی رمز عبور به صندوق ایمیل شما ارسال شد. صندوق ایمیل خود را بررسی کنید.'
                  )}
                </p>
              </div>
              <Link
                to="/sign-in"
                className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors mt-2"
              >
                <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                {t('auth.forgotPassword.backToSignIn', 'بازگشت به صفحه ورود')}
              </Link>
            </div>
          ) : (
            /* Form state */
            <>
              <div className="mb-6 space-y-1">
                <h2 className="text-base font-semibold text-foreground">
                  {t('auth.forgotPassword.title', 'فراموشی رمز عبور')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'auth.forgotPassword.subtitle',
                    'ایمیل حساب خود را وارد کنید تا لینک بازیابی برای شما ارسال شود.'
                  )}
                </p>
              </div>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('auth.signIn.email', 'ایمیل')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="example@hospital.ir"
                            autoComplete="email"
                            dir="ltr"
                            className="font-mono text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={mutation.isPending}
                  >
                    {mutation.isPending
                      ? t('auth.forgotPassword.sending', 'در حال ارسال...')
                      : t('auth.forgotPassword.send', 'ارسال لینک بازیابی')}
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/sign-in"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {t('auth.forgotPassword.backToSignIn', 'بازگشت به صفحه ورود')}
                    </Link>
                  </div>
                </form>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
